
/**
 * Created by rhinov15 on 17/03/2016.
*
* Node displaying rendering camera sprites
*/

import { EntityNode } from "./EntityNode";
import { CocosConstants } from "../../../../constants";
import { RotationNode } from "../Nodes/RotationNode";
import { PlanManager } from "../../../../cocosPlan";
import { Events, eventsManager, math, RenderCamera, SceneConstants } from "@rhinov/savane-js";

declare var cc: any;

export class CameraNode extends EntityNode {
    constructor(camera: RenderCamera) {
        // Call super class with entity
        super(camera);
        // Create node sprite
        this._sprite = new cc.Sprite();
        // Depending on the camera type, load the correct image texture
        switch ((this.savaneEntity as RenderCamera).cameraType) {
            case SceneConstants.CameraType.Panoramic:
                // Panoramic
                this.textureName = CocosConstants.resourcesBasePath + '/resources/pano.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/panoHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/panoSelected.png';
                break;

            case SceneConstants.CameraType.Axonomic:
                this.textureName = CocosConstants.resourcesBasePath + '/resources/axo.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/axoHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/axoSelected.png';
                break;

            case SceneConstants.CameraType.Video:
                this.textureName = CocosConstants.resourcesBasePath + '/resources/vid.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/vidHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/vidSelected.png';
                break;

            case SceneConstants.CameraType.Storage:
                this.textureName = CocosConstants.resourcesBasePath + '/resources/cameraBlue.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/cameraBlueHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/cameraSelected.png';
                break;

            default:
                // Others
                this.textureName = CocosConstants.resourcesBasePath + '/resources/camera.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/cameraHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/cameraSelected.png';
        }

        // And angle 0 facing top
        this.myAngle = 0;

        // Set sprite details and add it to the rendering
        this._sprite.setScale(0.30);
        this._sprite.setTexture(this.textureName);
        this.addChild(this._sprite, -1);

        // This node can be rotated so attach a rotation node to it
        this.rotateSprite = new RotationNode(this.savaneEntity);
        this.addChild(this.rotateSprite, 8);

        // Listener for camera selection
        this.selectedCameraListener = eventsManager.instance.addListener(Events.CAMERA_SELECTED, (event: any) => {
            // If the selected entity is the entity attached to the current node, then it is selected
            if (this.savaneEntity.id === event.userData) {
                this.setSelected(true);
            }
        });

        // Listener for the camera unselection
        this.unselectedCameraListener = eventsManager.instance.addListener(Events.CAMERA_UNSELECTED, (event: any) => {
            // If the selected entity is the entity attached to the current node, then it is unselected
            if (this.savaneEntity.id === event.userData) {
                this.setSelected(false);
            }
        });
    }

    // Return sprite width
    getWidth(): number {
        return this._sprite.width;
    }

    // Return sprite height
    getHeight(): number {
        return this._sprite.height;
    }

    // Update, get ready for next frame
    update(dt: number) {
        // Call super class update
        super.update(dt);

        // Update sprite representation if needed
        switch ((this.savaneEntity as RenderCamera).cameraType) {
            case SceneConstants.CameraType.Panoramic:
                // Panoramic
                this.textureName = CocosConstants.resourcesBasePath + '/resources/pano.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/panoHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/panoSelected.png';
                break;

            case SceneConstants.CameraType.Axonomic:
                this.textureName = CocosConstants.resourcesBasePath + '/resources/axo.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/axoHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/axoSelected.png';
                break;

            case SceneConstants.CameraType.Video:
                this.textureName = CocosConstants.resourcesBasePath + '/resources/vid.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/vidHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/vidSelected.png';
                break;

            case SceneConstants.CameraType.Storage:
                this.textureName = CocosConstants.resourcesBasePath + '/resources/cameraBlue.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/cameraBlueHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/cameraSelected.png';
                break;

            default:
                this.textureName = CocosConstants.resourcesBasePath + '/resources/camera.png';
                this.hoveredTextureName = CocosConstants.resourcesBasePath + '/resources/cameraHovered.png';
                this.selectedTextureName = CocosConstants.resourcesBasePath + '/resources/cameraSelected.png';
        }

        // Use the right texture image for the sprite
        if (this._isSelected) {
            this.rotateSprite.setVisible(true);
            this.rotateSprite.update();
            this._sprite.setTexture(this.selectedTextureName);
        }
        else {
            this.rotateSprite.setVisible(false);
            if (this._isHovered) {
                this._sprite.setTexture(this.hoveredTextureName);
            }
            else {
                this._sprite.setTexture(this.textureName);
            }
        }
    }

    // Say weither or not the sprite can rotate according to the cursor position
    toRotate(position: any) {
        // Just ask the rotation node if it can with the coordinates passed
        return (this.rotateSprite.toRotate(position));
    }

    // Set and get top rotation of the camera node
    setRotationZ(value: number) {
        if ((this.savaneEntity as RenderCamera).pitch !== undefined) {
            this.myAngle = Math.PI / 2;
        }
        else {
            this.myAngle = 0;
        }
        this.myAngle += value;

        this.setRotation((this.myAngle * 360) / (Math.PI * 2.0));
    }

    getRotationZ(): number {
        return this.myAngle;
    }

    getFrameworkPosition() {
        var layer = PlanManager.getInstance().scrollWindow;
        var position = layer.convertToNodeSpace(this.getPosition());
        var frameworkPosition = math.vec2.fromValues(position.x * 10, position.y * 10);

        return frameworkPosition;
    }

    // Remove the 3 listeners created in the constructor
    removeListeners() {
        eventsManager.instance.removeListener(this.toogleCameralistener);
        eventsManager.instance.removeListener(this.selectedCameraListener);
        eventsManager.instance.removeListener(this.unselectedCameraListener);
    }

    // Object destruction, remove the listeners
    onExit() {
        super.onExit();
        this.removeListeners();
    }
}
